import type { ErrorDefinition, FriendlyError } from './types';

/**
 * Subscription-related error definitions.
 * Maps backend error codes (UPPERCASE, from /api/subscription/*) and
 * client-side fallback codes (lowercase) to user-friendly messages.
 */
export const SUBSCRIPTION_ERRORS: Record<string, ErrorDefinition> = {
  // ---------------------------------------------------------
  // Backend codes (UPPERCASE, sent in apiError.code)
  // ---------------------------------------------------------
  AUTHENTICATION_ERROR: {
    title: 'Session expired',
    message: 'Your session has expired.',
    action: 'Please sign in again to continue.',
    retryable: false,
    category: 'authentication',
  },

  VALIDATION_ERROR: {
    title: 'Invalid request',
    message: "Some of the billing details didn't look right.",
    action: 'Please try again.',
    retryable: true,
    category: 'validation',
  },

  PAYMENT_ERROR: {
    title: 'Payment service issue',
    message: "We're having trouble reaching our payment processor.",
    action: 'Please try again in a few moments.',
    retryable: true,
    retryAfterSeconds: 5,
    category: 'server',
  },

  CONFLICT: {
    title: 'Already subscribed',
    message: 'You already have an active subscription.',
    action: 'Manage your plan from the billing portal.',
    retryable: false,
    category: 'account',
  },

  NOT_FOUND: {
    title: 'No subscription found',
    message: "We couldn't find an active subscription on your account.",
    action: 'Visit the pricing page to start a subscription.',
    retryable: false,
    category: 'account',
  },

  INTERNAL_ERROR: {
    title: 'Something went wrong',
    message: 'We had trouble reaching our billing service.',
    action: 'Please try again in a few moments.',
    retryable: true,
    retryAfterSeconds: 5,
    category: 'server',
  },

  CONFIG_ERROR: {
    title: 'Plans unavailable',
    message: "Subscription plans aren't configured right now.",
    action: 'Please try again later or contact support.',
    retryable: false,
    category: 'server',
  },

  // ---------------------------------------------------------
  // Client fallback codes (lowercase, used when apiError.code is missing)
  // ---------------------------------------------------------
  prices_load_failed: {
    title: "Couldn't load plans",
    message: 'We had trouble loading subscription plans.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  subscription_load_failed: {
    title: "Couldn't load subscription",
    message: 'We had trouble loading your subscription details.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  checkout_failed: {
    title: "Couldn't start checkout",
    message: 'We had trouble opening checkout.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  portal_failed: {
    title: "Couldn't open billing portal",
    message: 'We had trouble opening the billing portal.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  network_offline: {
    title: "You're offline",
    message: 'Managing your subscription requires an internet connection.',
    action: 'Please connect to the internet and try again.',
    retryable: true,
    category: 'network',
  },
};

/**
 * Get a friendly error for a subscription-related error code.
 * Returns the offline entry when the device is offline, regardless of input code.
 */
export function getSubscriptionError(code: string): FriendlyError {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { ...SUBSCRIPTION_ERRORS.network_offline, code: 'network_offline' };
  }

  const errorDef = SUBSCRIPTION_ERRORS[code] ?? {
    title: 'Something went wrong',
    message: 'An unexpected error occurred.',
    action: 'Please try again.',
    retryable: true,
    category: 'unknown' as const,
  };

  return {
    ...errorDef,
    code,
  };
}
